import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { CustomReport, CreateReportRequest, PaginatedResponse, ApiResponse } from 'types';
import { dateStringToEpoch, dateStringToEpochEndOfDay } from 'utils/helpers';

export const reportsService = {
  /**
   * Get list of custom reports
   */
  async getReports(params?: { page?: number; perPage?: number; organizationId?: string }): Promise<ApiResponse<PaginatedResponse<CustomReport>>> {
    try {
      const response = await axiosInstance.get<ApiResponse<PaginatedResponse<CustomReport>>>(
        API_ENDPOINTS.CUSTOM_REPORTS,
        // Convert frontend 1-based page to backend 0-based page
        { params: { ...params, page: params?.page !== undefined ? Math.max(0, params.page - 1) : 0 } }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch reports'
      };
    }
  },

  /**
   * Create a new custom report
   */
  async createReport(request: CreateReportRequest): Promise<ApiResponse<CustomReport>> {
    try {
      // Convert date strings to epoch for the API
      const apiRequest = {
        report_type: request.report_type,
        date_from: dateStringToEpoch(request.date_from),
        date_to: dateStringToEpochEndOfDay(request.date_to),
        organization_id: request.organization_id
      };

      const response = await axiosInstance.post<ApiResponse<CustomReport>>(
        API_ENDPOINTS.CUSTOM_REPORTS,
        apiRequest
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create report'
      };
    }
  },

  /**
   * Get report details by ID
   */
  async getReportDetail(id: number, organizationId?: string): Promise<ApiResponse<CustomReport>> {
    try {
      const response = await axiosInstance.get<ApiResponse<CustomReport>>(
        API_ENDPOINTS.CUSTOM_REPORT_DETAIL(id),
        { params: { organizationId } }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch report details'
      };
    }
  },

  /**
   * Download a completed report
   */
  async downloadReport(id: number, reportName: string): Promise<void> {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.CUSTOM_REPORT_DOWNLOAD(id),
        { responseType: 'blob' }
      );

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportName}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to download report');
    }
  },

  /**
   * Delete a report
   */
  async deleteReport(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await axiosInstance.delete<ApiResponse<void>>(
        API_ENDPOINTS.CUSTOM_REPORT_DETAIL(id)
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete report'
      };
    }
  }
};
